import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TextInput, FormErrors, PasswordInput, Button } from 'components/ui';
import { logInFormSchema, LogInFormData } from 'forms';
import { useAppForm } from 'hooks';
import { useRouter } from 'next/router';
import React, { ReactElement, useEffect, useState } from 'react';
import { useAuthApi, useSamlApi } from 'services/api';
import {
  captureLoginReturnTo,
  consumeLoginReturnTo,
} from 'util/login-return-to';

export const LogInForm = (): ReactElement => {
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [email, setEmail] = useState('');

  const isMobile = !useMediaQuery('(min-width:900px)');
  const theme = useTheme();

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
  } = useAppForm(logInFormSchema);

  const router = useRouter();
  const authApi = useAuthApi();
  const samlApi = useSamlApi();

  useEffect(() => {
    captureLoginReturnTo(router.query.next);
  }, [router.query.next]);

  const handleEmailContinue = async () => {
    const emailValue = getValues('email') as string;

    if (!emailValue || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailValue)) {
      setError('email', {
        type: 'manual',
        message: 'Please enter a valid email address.',
      });
      return;
    }

    try {
      const result = await samlApi.getSamlSchoolForEmail(emailValue);
      const samlData = result.success ? result.value : null;

      if (samlData?.success && samlData.redirectUrl) {
        const next = captureLoginReturnTo(router.query.next);
        const redirectUrl = next
          ? `${samlData.redirectUrl}${
              samlData.redirectUrl.includes('?') ? '&' : '?'
            }next=${encodeURIComponent(next)}`
          : samlData.redirectUrl;
        window.location.href = redirectUrl;
        return;
      }
    } catch (error) {
      console.error('SAML check failed:', error);
    }

    setEmail(emailValue);
    setStep('password');
  };

  const onSubmit = async (data: LogInFormData) => {
    const result = await authApi.logIn(data);

    if (!result.success) {
      if (result.error.response?.status === 401) {
        const message = 'Some of your info is not correct. Please try again.';
        setError('general', {
          type: 'manual',
          message,
        });
      } else {
        setError('general', {
          message: result.error.response?.data.message as string,
        });
      }
      return;
    }
    await router.push(consumeLoginReturnTo());
  };

  const handleBack = () => {
    setStep('email');
    setValue('password', '');
    setError('password', { type: 'manual', message: '' });
  };

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      width={{ xs: '100%', md: '80%' }}
      height={isMobile ? '100vh' : 'auto'}
      minHeight={isMobile ? '100vh' : 'auto'}
      bgcolor={theme => theme.palette.background.default}
      borderRadius={isMobile ? 0 : 4}
      overflow="hidden"
    >
      <Box
        width={{ xs: '100%', md: '100%' }}
        height={{ xs: 240, md: 'auto' }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={isMobile ? 3 : 8}
        bgcolor={theme => theme.palette.background.default}
        sx={{
          mt: isMobile ? 48 : 0,
        }}
      >
        {step === 'email' && (
          <form
            style={{ width: '100%' }}
            onSubmit={e => {
              e.preventDefault();
              handleEmailContinue();
            }}
          >
            <Typography
              variant="h5"
              mb={{ xs: 16, md: 5 }}
              fontWeight={600}
              sx={{ textAlign: 'left' }}
            >
              Log in
            </Typography>
            <TextInput
              label="Email"
              placeholder="name@school.edu"
              hasError={!!errors.email}
              {...register('email', { required: true })}
              defaultValue={email}
              type="email"
            />
            <FormErrors>{errors.email?.message}</FormErrors>
            <Button type="submit" minWidth="100%" variant="vibrant">
              Continue
            </Button>
            <Typography
              mt={16}
              fontSize={14}
              color={theme => theme.palette.text.primary}
              sx={{ textAlign: 'left' }}
            >
              Don&apos;t have an account yet?{' '}
              <a
                href="/signup"
                style={{
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                }}
              >
                Sign up now
              </a>
            </Typography>
            <Typography mt={16} fontSize={10} color={theme => theme.palette.text.primary} sx={{ textAlign: 'left' }}>
              This site is protected by reCAPTCHA and the Google{' '}
                <a style={{ textDecoration: 'underline', color: theme.palette.primary.main }} href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and{' '}
                <a style={{ textDecoration: 'underline', color: theme.palette.primary.main }} href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a> apply.
            </Typography>
          </form>
        )}
        {step === 'password' && (
          <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
            <Box
              display="flex"
              alignItems="flex-start"
              mb={{ xs: 16, md: 5 }}
              gap={1}
              sx={{
                textAlign: 'left',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{
                  mb: 0,
                  fontSize: 22,
                  fontWeight: 500,
                  lineHeight: 1.2,
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                Enter your password
              </Typography>
              <Typography
                fontSize={15}
                color={theme => theme.palette.text.primary}
                sx={{
                  fontWeight: 400,
                  mt: 16,
                  mb: 16,
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                Logging in as {email}
              </Typography>
            </Box>
            <PasswordInput
              label="Password"
              type="password"
              autoComplete="current-password"
              hasError={!!errors.password || !!errors.general}
              {...register('password', { required: true })}
              autoFocus
            />
            <FormErrors>
              {errors.password?.message || errors.general?.message}
            </FormErrors>
            <Button
              type="submit"
              minWidth="100%"
              variant="vibrant"
              disabled={isSubmitting}
            >
              Continue
            </Button>
            <Box display="flex" justifyContent="space-between" mt={16}>
              <Typography
                fontSize={13}
                color={theme => theme.palette.text.primary}
                style={{ cursor: 'pointer' }}
                onClick={handleBack}
              >
                Back
              </Typography>
              <a
                href="/reset-password"
                style={{
                  fontSize: 13,
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                }}
              >
                Forgot Password?
              </a>
            </Box>
          </form>
        )}
      </Box>
    </Box>
  );
};

export default LogInForm;
